import { useHeaderHeight } from '@react-navigation/elements';
import * as Linking from 'expo-linking';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { type LegacyRef, useEffect, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, type TextInput } from 'react-native';
import {
    AlertTriangleIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Regular,
    Icon,
    Input,
    isWeb,
    KeyIcon,
    Private,
    SafeAreaView,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { getWebUrl } from '../../../lib/Web';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException, captureMessage } from '../../../logger/Logger';
import { resetPassword } from '../../api/AuthApi';
import { checkPasswordStrength } from '../../stytchHelpers';
import { ResetPasswordSource } from '../../types/Auth';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';

const ResetPassword = () => {
    LogRocketFacade.tagPage('ResetPassword');

    const toast = useToastController();
    const headerHeight = useHeaderHeight();
    const { token, source_id } = useLocalSearchParams<{
        token: string;
        source_id: string;
    }>();
    const [submitEnabled, setSubmitEnabled] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState('');

    const passwordInput: LegacyRef<TextInput> | null = useRef(null);

    useEffect(() => {
        setSubmitEnabled(password.trim().length > 0);
    }, [password]);

    // Until password reset flows are fixed on native, redirect to web.
    // See ForgotPasswordStart.tsx for more context.
    if (!isWeb) {
        captureMessage('Navigating to password reset flow on web.');
        const url = `${getWebUrl()}/reset-password?token=${token}&source_id=${source_id}`;
        void Linking.openURL(url);
        return <Redirect href="/" />;
    }

    if (token == null) {
        captureMessage('No token found, redirecting to sign in.');
        return <Redirect href="/sign-in" />;
    }

    /* Redirect to partner enrollment page */
    if (
        source_id === (ResetPasswordSource.PARTNER_MEMBER_INVITATION as string)
    ) {
        return (
            <Redirect
                href={{
                    pathname: '/start-membership/partner',
                    params: { token },
                }}
            />
        );
    }

    /* Redirect a returning member to finish reactivating their account.
       Nested under /sign-up/new-member/* since that's the only /sign-up/*
       prefix Netlify's _redirects serves from the SPA — everything else
       under /sign-up/* proxies to the webflow marketing site instead. */
    if (source_id === (ResetPasswordSource.RETURNING_MEMBER as string)) {
        return (
            <Redirect
                href={{
                    pathname: '/sign-up/new-member/returning-info',
                    params: { token },
                }}
            />
        );
    }

    // On web, only allow member-initiated password resets until the other flows (partner member enrollment and joint account holder enrollment) are implemented.
    // This block should not be hit, since redirects for these cases are handled via Netlify _redirects.
    if (source_id !== (ResetPasswordSource.RESET_PASSWORD as string)) {
        captureException(
            new Error(
                'Attempt to perform a non-supported password reset flow.',
            ),
        );
        return <Redirect href="/" />;
    }

    const submitPassword = async () => {
        try {
            setSubmitEnabled(false);

            const trimmedPassword = password.trim();
            const checkPasswordStrengthResponse =
                await checkPasswordStrength(trimmedPassword);
            const isPasswordValid = checkPasswordStrengthResponse.validPassword;
            if (!isPasswordValid) {
                return;
            }

            await resetPassword({
                password: trimmedPassword,
                token,
            });

            // Redirect to sign-in page to log in with new password
            router.navigate('/sign-in');
            toast.show('Password updated successfully. Please sign in.', {
                icon: (
                    <View
                        padding={12}
                        backgroundColor="$gray100"
                        borderRadius={8}
                    >
                        <Icon icon={KeyIcon} size={24} color="$gray1200" />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);

            toast.show('Error updating password', {
                icon: (
                    <View
                        padding={12}
                        backgroundColor="$gray100"
                        borderRadius={8}
                    >
                        <Icon
                            icon={AlertTriangleIcon}
                            size={24}
                            color="$gray1200"
                        />
                    </View>
                ),
            });
        } finally {
            setSubmitEnabled(true);
        }
    };

    return (
        <SafeAreaView style={{ flex: 1 }} testID="reset-password-page">
            <KeyboardAvoidingView
                style={{
                    flex: 1,
                    height: '100%',
                    ...(isWeb && {
                        justifyContent: 'center',
                        alignItems: 'center',
                    }),
                }}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={headerHeight + 16}
            >
                <YStack
                    style={{ width: '100%' }}
                    maxWidth={450}
                    gap={12}
                    marginTop={82}
                    marginHorizontal={20}
                    paddingHorizontal={8}
                >
                    <H5Regular>Please create a new password</H5Regular>
                    <View>
                        <Private>
                            <Input
                                ref={passwordInput}
                                autoCapitalize="none"
                                height={54}
                                padding={16}
                                borderRadius={4}
                                borderColor="$gray200"
                                key="password"
                                placeholder="Password"
                                inputMode="text"
                                secureTextEntry={!showPassword}
                                keyboardType="default"
                                textContentType="password"
                                value={password}
                                onChangeText={setPassword}
                                onSubmitEditing={submitPassword}
                                blurOnSubmit={false}
                                paddingRight={48}
                            />
                        </Private>
                        <ShowPasswordToggle
                            showPassword={showPassword}
                            setShowPassword={setShowPassword}
                        />
                    </View>
                    <PasswordStrengthChecker password={password} />

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={submitPassword}
                        disabled={!submitEnabled}
                        buttonProps={{
                            marginTop: 'auto',
                        }}
                    />
                </YStack>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
};

export default ResetPassword;
